import Api from '@/api';
import { createRequestSaga } from '@/redux/helpers';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
  requestSoRepsAsync,
  requestSoStatsAsync,
  requestSoTeamsAsync,
  exportSoRepsAsync,
  requestSoSeasonsAsync,
  updateRepsStatusAsync,
} from './actions';
import { addToastsAction } from '@/redux/toasts';
import { mapErrorToastsData, mapSuccessToastsData } from '@/lib/api';
import { prepareDataForUpdateRepsStatus } from '@/modules/dashboard/lib';

function* requestSoStatsSaga({ payload }) {
  const response = yield call(
    Api.getSoStats,
    { ...payload },
  );

  yield put(requestSoStatsAsync.success(response));
}

function* requestSoRepsSaga({ payload }) {
  try {
    const response = yield call(
      Api.getSoReps,
      { ...payload },
    );

    yield put(requestSoRepsAsync.success(response));
  } catch (error) {
    yield put(addToastsAction(mapErrorToastsData(error)));

    throw error;
  }
}

function* requestSoTeamsSaga() {
  const response = yield call(
    Api.getSoTeams,
  );

  yield put(requestSoTeamsAsync.success(response));
}

function* exportSoRepsSaga({ payload }) {
  const { selectedReps, params } = payload;
  try {
    const response = yield call(
      Api.exportSoReps,
      {
        ids: selectedReps,
        ...params,
      },
    );

    // Get current date for the filename
    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().split('T')[0]; // Format: YYYY-MM-DD
    const filename = `Rep Export - ${formattedDate}.csv`;

    // Create a blob URL from the response with CSV mime type
    const blob = new Blob([response], {
      type: 'text/csv',
    });
    const url = window.URL.createObjectURL(blob);

    // Use the filename with date
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    yield put(exportSoRepsAsync.success({ success: true }));
  } catch (error) {
    yield put(addToastsAction(mapErrorToastsData(error)));
    yield put(exportSoRepsAsync.failure(error));
    throw error;
  }
}

function* requestSoSeasonsSaga() {
  const response = yield call(
    Api.getSoSeasons,
  );

  yield put(requestSoSeasonsAsync.success(response));
}

function* updateRepsStatusSaga({ payload }) {
  try {
    const { statusParams, onSuccess } = payload;
    const data = prepareDataForUpdateRepsStatus(statusParams);
    const response = yield call(
      Api.updateRepsStatus,
      { ...data },
    );

    yield put(addToastsAction(mapSuccessToastsData(response)));
    onSuccess();
  } catch (error) {
    yield put(addToastsAction(mapErrorToastsData(error)));

    throw error;
  }
}

export function* soActionWatcher() {
  yield takeLatest(
    requestSoStatsAsync.request,
    createRequestSaga(requestSoStatsSaga, {
      keyNew: 'soStats',
      errKey: 'soStats',
      write: false,
    }),
  );

  yield takeLatest(
    requestSoRepsAsync.request,
    createRequestSaga(requestSoRepsSaga, {
      keyNew: 'soReps',
      errKey: 'soReps',
      write: false,
    }),
  );

  yield takeLatest(
    requestSoTeamsAsync.request,
    createRequestSaga(requestSoTeamsSaga, {
      keyNew: 'soTeams',
      errKey: 'soTeams',
      write: false,
    }),
  );

  yield takeLatest(
    exportSoRepsAsync.request.type,
    createRequestSaga(exportSoRepsSaga, {
      keyNew: 'soExport',
      errKey: 'soExport',
      write: false,
    }),
  );

  yield takeLatest(
    requestSoSeasonsAsync.request,
    createRequestSaga(requestSoSeasonsSaga, {
      keyNew: 'soSeasons',
      errKey: 'soSeasons',
      write: false,
    }),
  );

  yield takeLatest(
    updateRepsStatusAsync.request,
    createRequestSaga(updateRepsStatusSaga, {
      keyNew: 'updateStatus',
      errKey: 'updateStatus',
      write: false,
    }),
  );
}
